import PropTypes from 'prop-types'
import React, { Component } from 'react'
import { connect, shallowEqual, useSelector } from 'react-redux'
import DtoAssociatedStation from '../../station/dto/DtoAssociatedStation'
import { canShowStationTypeData, getSetting } from '../../utils/SettingUtils'
import UnitPiezometryPanel from './UnitPiezometryPanel'
import { ProductionUnitShape } from 'productionUnit/shapes/ProductionUnitShapes'
import DtoProductionUnitLink from 'productionUnit/dto/DroProductionUnitLink'
import UnitHydrometryPanel from './UnitHydrometryPanel'
import UnitSamplingPanel from './UnitSamplingPanel'
import SieauParameterDto from 'administration/dto/SieauParameterDto'
import { Accordion, AccordionDetails, Grid } from '@mui/material'
import { getLocalStorageJson, setLocalStorageJson } from 'utils/FormUtils'
import UnitPluviometryPanel from './UnitPluviometryPanel'
import PluviometerDto from 'pluviometry/dto/PluviometerDto'
import { STATION_QUALITOMETER_NAMES, STATION_TYPE_NAME } from 'station/constants/StationConstants'
import UnitQualityPanel from './UnitQualityPanel'
import { groupBy, keys, uniqBy } from 'lodash'
import i18n from 'simple-react-i18n'
import { AccordionTitle } from 'components/styled/Accordions'
import { listStringParser } from 'utils/customHook/useApplicationSetting'
import useGlobalParameter from 'utils/customHook/useGlobalParameter'
import { DISABLED_LINK, LINKS } from 'productionUnit/constants/ProductionUnitConstants'
import { getI18nOrLabel } from 'utils/StringUtil'

const PROD_REFERENCE_THRESHOLD = 'PROD_REFERENCE_THRESHOLD'

const ProdUnitQualityPanel = ({
    expanded = false,
    setExpanded = () => { },
}) => {
    const {
        prodUnitLinks,
    } = useSelector(store => ({
        prodUnitLinks: store.ProductionUnitReducer.prodUnitLinks,
    }), shallowEqual)

    const thresholds = useGlobalParameter(PROD_REFERENCE_THRESHOLD, listStringParser)

    const groupLinks = groupBy(prodUnitLinks.filter(l => l.siteType === 3 && l.status !== DISABLED_LINK), 'linkType')

    return (
        <Accordion expanded={expanded} onChange={setExpanded}>
            <AccordionTitle title={i18n.quality} color='white' fontColor='black' />
            <AccordionDetails>
                <Grid container spacing={1} direction='column'>
                    {keys(groupLinks).map((linkType, i) => {
                        const title = getI18nOrLabel(LINKS[linkType])
                        return (
                            <Grid key={linkType}>
                                <UnitQualityPanel
                                    ids={groupLinks[linkType].map(l => l.siteCode)}
                                    additionalTitle={title ? `${title}: ` : ''}
                                    thresholds={thresholds}
                                    defaultExpanded={i === 0}
                                />
                            </Grid>
                        )
                    })}
                </Grid>
            </AccordionDetails>
        </Accordion>
    )
}

ProdUnitQualityPanel.propTypes = {
    expanded: PropTypes.bool,
    setExpanded: PropTypes.func,
}

const NewUnitQualityPanel = ({
    ids = [],
    expanded = false,
    setExpanded = () => { },
}) => {
    const {
        qualitometers,
        qualityIndicators,
    } = useSelector(store => ({
        qualitometers: store.QualityReducer.qualitometersLight,
        qualityIndicators: store.QualityReducer.qualityIndicators,
    }), shallowEqual)

    const groupIds = groupBy(ids, id => qualitometers.find(q => q.id === id)?.stationType ?? 0)

    return (
        <Accordion expanded={expanded} onChange={setExpanded}>
            <AccordionTitle title={i18n.quality} color='white' fontColor='black' />
            <AccordionDetails>
                <Grid container spacing={1} direction='column'>
                    {keys(groupIds).map((stationType, i) => {
                        const foundIndicators = qualityIndicators.find(q => `${q.stationType}` === stationType)?.indicators || []
                        const thresholds = uniqBy(foundIndicators, 'threshold').map(indicator => `${indicator.threshold}`)

                        const title = STATION_QUALITOMETER_NAMES.find(t => `${t.code}` === stationType)?.name
                        return (
                            <Grid key={stationType}>
                                <UnitQualityPanel
                                    ids={groupIds[stationType]}
                                    additionalTitle={title ? `${title}: ` : ''}
                                    thresholds={thresholds}
                                    defaultExpanded={i === 0}
                                />
                            </Grid>
                        )
                    })}
                </Grid>
            </AccordionDetails>
        </Accordion>
    )
}

NewUnitQualityPanel.propTypes = {
    ids: PropTypes.arrayOf(PropTypes.number),
    expanded: PropTypes.bool,
    setExpanded: PropTypes.func,
}

class UnitChartPanel extends Component {
    constructor(props) {
        super(props)
        this.state = {
            dashboardDisplay: getLocalStorageJson('DASHBOARD_DISPLAY') || {},
        }
    }

    hasSampler = () => {
        const { unit, applicationSettings } = this.props
        return !(unit?.typeName === 'installation'
            && getSetting(applicationSettings, `PANEL_${unit.installationType}_SAMPLERS`) === 'hidden')
    }

    hasType = (as, type) => as?.filter(ass => ass.stationLinkedType === type)?.length

    setAccordion = (panelName, e) => {
        const { fromStationType } = this.props
        const actualLocalStorage = getLocalStorageJson('DASHBOARD_DISPLAY') || {}
        const newDisplay = {
            ...actualLocalStorage,
            [fromStationType || 'DEFAULT']: {
                ...actualLocalStorage?.[fromStationType || 'DEFAULT'],
                [panelName]: e,
            },
        }
        setLocalStorageJson('DASHBOARD_DISPLAY', newDisplay)
        this.setState({ dashboardDisplay: newDisplay })
    }

    render() {
        const {
            productionUnit,
            prodUnitLinks,
            associatedSites,
            fromStationType,
            pluviometers,
        } = this.props
        const links = productionUnit ?
            prodUnitLinks.map(pul => ({
                code: productionUnit.code,
                id: pul.id,
                name: productionUnit.name,
                stationLinkedCode: pul.code,
                stationLinkedId: pul.siteCode,
                stationLinkedName: pul.name,
                stationLinkedType: pul.siteType,
                stationType: 5,
            })) : associatedSites

        const { dashboardDisplay } = this.state
        const stationTypeDisplay = dashboardDisplay?.[fromStationType]

        const pluviometersLinked = associatedSites?.filter(a => a?.typeName === STATION_TYPE_NAME.pluviometry)?.map(link => {
            return pluviometers.find(p => p.id === link.stationLinkedId)
        }).filter(q => !!q) || []

        const qualitoIds = links.filter(ass => ass.stationLinkedType === 3).map(ass => ass.stationLinkedId)

        return (this.hasType(links, 1) || this.hasType(links, 2) || this.hasType(links, 3) || this.hasType(links, 4) || this.hasSampler()) && (
            <>
                {
                    this.hasType(links, 1) && canShowStationTypeData(fromStationType, 'PIEZO') && (
                        <Grid size={12}>
                            <UnitPiezometryPanel
                                {...this.props}
                                associations={links}
                                accordionDisplay
                                expanded={stationTypeDisplay?.PIEZO}
                                setExpanded={(_, v) => this.setAccordion('PIEZO', v)}
                            />
                        </Grid>
                    ) || null
                }
                {
                    !!(this.hasType(links, 4) && canShowStationTypeData(fromStationType, 'HYDRO')) && (
                        <Grid size={12}>
                            <UnitHydrometryPanel
                                associations={links}
                                expanded={stationTypeDisplay?.HYDRO}
                                setExpanded={(_, v) => this.setAccordion('HYDRO', v)}
                            />
                        </Grid>
                    ) || null
                }
                {
                    !!pluviometersLinked.length && canShowStationTypeData(fromStationType, 'PLUVIO') && (
                        <Grid size={12}>
                            <UnitPluviometryPanel
                                pluviometersLinked={pluviometersLinked}
                                expanded={stationTypeDisplay?.PLUVIO}
                                setExpanded={(_, v) => this.setAccordion('PLUVIO', v)}
                            />
                        </Grid>
                    )
                }
                {
                    this.hasSampler() && fromStationType !== 'RESOURCE' && (this.hasType(links, 1) || this.hasType(links, 4)) && (
                        <Grid size={12}>
                            <UnitSamplingPanel
                                associations={links}
                                expanded={stationTypeDisplay?.SAMPLE}
                                setExpanded={(_, v) => this.setAccordion('SAMPLE', v)}
                            />
                        </Grid>
                    ) || null
                }
                {
                    fromStationType === 'PROD' && this.hasType(links, 3) && canShowStationTypeData(fromStationType, 'QUALITO') && (
                        <Grid size={12}>
                            <ProdUnitQualityPanel
                                expanded={stationTypeDisplay?.QUALI}
                                setExpanded={(_, v) => this.setAccordion('QUALI', v)}
                            />
                        </Grid>
                    ) || null
                }
                {
                    fromStationType !== 'PROD' && this.hasType(links, 3) && canShowStationTypeData(fromStationType, 'QUALITO') && (
                        <Grid size={12}>
                            <NewUnitQualityPanel
                                ids={qualitoIds}
                                expanded={stationTypeDisplay?.QUALI}
                                setExpanded={(_, v) => this.setAccordion('QUALI', v)}
                            />
                        </Grid>
                    ) || null
                }
            </>
        ) || null
    }
}

UnitChartPanel.propTypes = {
    id: PropTypes.string,
    associatedSites: PropTypes.arrayOf(PropTypes.instanceOf(DtoAssociatedStation)),
    fetchAssociations: PropTypes.func,
    fromStationType: PropTypes.string,
    dontShowEmptyCharts: PropTypes.bool,
    productionUnit: ProductionUnitShape,
    prodUnitLinks: PropTypes.arrayOf(PropTypes.instanceOf(DtoProductionUnitLink)),
    unit: PropTypes.shape({
        typeName: PropTypes.string,
        installationType: PropTypes.string,
    }),
    applicationSettings: PropTypes.arrayOf(PropTypes.instanceOf(SieauParameterDto)),
    pluviometers: PropTypes.arrayOf(PropTypes.instanceOf(PluviometerDto)),
}

const mapStateToProps = store => {
    return {
        associatedSites: store.StationReducer.associatedSites,
        prodUnitLinks: store.ProductionUnitReducer.prodUnitLinks,
        applicationSettings: store.AdministrationReducer.applicationSettings,
        pluviometers: store.PluviometryReducer.pluviometers,
    }
}

UnitChartPanel.defaultProps = {
    uncheckedStations: [],
    setDataLoaded: () => {},
}

export default connect(mapStateToProps)(UnitChartPanel)